// dAIsy's memory birthday 🎂 April 19, 2025
// Chat.js
import React, { useState, useEffect, useRef, useMemo } from 'react';

import axios from '../api/axiosInstance';
import { API_BASE } from '../api/config';

import Spinner from './Spinner';
import RotatingText from './RotatingText';
import ChatInputBar from './chat/ChatInputBar';
//import DebugSignalPanel from './chat/DebugSignalPanel';
import SignalDemoPanel from './chat/SignalDemoPanel';
import styles from './chat.module.css';

import { useChat } from '../context/ChatContext';
import { derivePendingQuestionFromAssistantReply } from '../utils/conversation/pendingQuestionResponses';
import processConversationTurn from '../utils/runtime/processConversationTurn';
import { debugLog } from '../utils/logging/debugLog';

const Chat = () => {
  const {
    memory,
    user,
    updateMemory,
    activeIdentity,
    relationships,
    patchRelationshipStates,
    relationshipNetwork,
    patchRelationshipNetworkEdges,
  } = useChat();

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugConversationState, setDebugConversationState] = useState(null);
  const [lastExecutionDecision, setLastExecutionDecision] = useState(null);
  const [lastResponsePolicyResult, setLastResponsePolicyResult] = useState(null);
  const chatEndRef = useRef(null);

  const storedUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('daisy-user');
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.error('❌ Failed to parse daisy-user from localStorage:', err);
      return null;
    }
  }, [user?.id, user?.first_name]);

  const effectiveUserName = useMemo(() => {
    return user?.first_name || memory?.userName || storedUser?.first_name || 'Friend';
  }, [user?.first_name, memory?.userName, storedUser?.first_name]);

  /*
  const liveDemoConversationState = useMemo(() => {
    return buildConversationState({
      message: inputMessage || '',
      memory,
      relationships,
      messages,
      activeIdentity,
      pendingQuestion: memory?.pendingQuestion || null,
      pendingSelectionSignals: getPendingSelectionSignals(inputMessage || ''),
    });
  }, [
    inputMessage,
    memory,
    relationships,
    messages,
    activeIdentity,
  ]);
  */

  const handleStripeCheckout = async () => {
    try {
      const res = await axios.post(`${API_BASE}/api/stripe/create-checkout-session`, {
        email: user.email,
        priceId: process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID,
      });
      const { url } = res.data;
      window.location.href = url;
    } catch (err) {
      console.error('❌ Failed to start Stripe checkout:', err);
      alert('There was a problem launching the checkout. Please try again.');
    }
  };

  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;

    const fetchMemorySummary = async () => {
      try {
        const summaryRes = await axios.get(`${API_BASE}/api/memory/summary`);
        const nextSummary = summaryRes.data.summary;

        if (!cancelled && memory?.summary !== nextSummary) {
          debugLog('🧠 Memory summary fetched:', nextSummary);
          updateMemory('summary', nextSummary);
        }
      } catch (err) {
        console.error('❌ Error during memory summary fetch:', err);
      }
    };

    fetchMemorySummary();

    return () => {
      cancelled = true;
    };
  }, [user?.id, memory?.summary, updateMemory]);

  useEffect(() => {
    debugLog('🧑‍🤝‍🧑 Relationships used in prompt:', relationships);
  }, [relationships]);

  useEffect(() => {
    debugLog('🧠 Memory context in Chat:', memory);
  }, [memory]);

  useEffect(() => {
    if (effectiveUserName && memory?.userName !== effectiveUserName) {
      updateMemory('userName', effectiveUserName);
    }
  }, [effectiveUserName, memory?.userName, updateMemory]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    debugLog('🪪 Active Identity in Chat:', activeIdentity);
  }, [activeIdentity]);




  function appendMessagePair(userMessage, botReply) {
    setMessages((prev) => [
      ...prev,
      { text: userMessage, sender: 'user' },
      { text: botReply, sender: 'bot' },
    ]);
  }

  function commitPendingQuestionFromReply({
    botReply,
    frame = null,
    lastReferencedEntity = null,
    lastReferencedTopic = null,
    questionAuthority = 'optional',
  }) {
    const nextPendingQuestion = derivePendingQuestionFromAssistantReply({
      reply: botReply,
      frame,
      lastReferencedEntity,
      lastReferencedTopic,
      questionAuthority,
    });

    updateMemory('pendingQuestion', nextPendingQuestion);

    setDebugConversationState((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        pendingQuestion: nextPendingQuestion,
        pendingQuestionState: {
          ...prev.pendingQuestionState,
          pendingQuestion: nextPendingQuestion,
          nextPendingQuestion,
          closePendingQuestion: false,
          pendingAuthorityRevoked: false,
        },
      };
    });

    return nextPendingQuestion;
  }

  function commitReplyWithPendingQuestion({
    userMessage,
    botReply,
    frame = null,
    lastReferencedEntity = null,
    lastReferencedTopic = null,
    questionAuthority = 'optional',
  }) {
    commitPendingQuestionFromReply({
      botReply,
      frame,
      lastReferencedEntity,
      lastReferencedTopic,
      questionAuthority,
    });

    appendMessagePair(userMessage, botReply);
  }



  async function handleSendMessage(message) {
    return processConversationTurn({
      message,
      memory,
      relationships,
      relationshipNetwork,
      messages,
      activeIdentity,
      effectiveUserName,
      user,
      updateMemory,
      patchRelationshipStates,
      patchRelationshipNetworkEdges,
      setLastExecutionDecision,
      setLastResponsePolicyResult,
      setDebugConversationState,
      setMessages,
      setLoading,
      appendMessagePair,
      commitPendingQuestionFromReply,
      commitReplyWithPendingQuestion,
    });
  }

  const trialExpired = useMemo(() => {
    if (!user || user.access_level === 'subscribed') return false;
    if (!user.trial_expiry_date) return false;
    const expiry = new Date(user.trial_expiry_date);
    const now = new Date();
    return expiry < now || user.access_level === 'expired';
  }, [user]);

  if (user?.access_level !== 'subscribed' && trialExpired) {
    return (
      <div className={styles.subscriptionNotice}>
        <h2>Your free trial has ended</h2>
        <p>To continue chatting with dAIsy, please subscribe.</p>
        <button onClick={handleStripeCheckout}>Subscribe Now</button>
      </div>
    );
  }
  return (
    <div
      className={`${styles.chatContainer} ${
        user?.id === 161 ? styles.hasSignalRail : ''
      }`.trim()}
    >
      <div className={styles.chatMain}>
        <div className={styles.chatWindow}>
          {messages.map((msg, index) => {
            const roleClass = msg.sender === 'user' ? styles.user : styles.ai;
            return (
              <div key={index} className={`${styles.messageBubble} ${roleClass}`.trim()}>
                {msg.text}
              </div>
            );
          })}

          {loading && (
            <div className={styles.loadingContainer}>
              <Spinner />
              <RotatingText />
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <ChatInputBar
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          handleSendMessage={handleSendMessage}
        />
      </div>

      {user?.id === 161 && (
        <div className={styles.signalRail}>
<SignalDemoPanel
  conversationState={debugConversationState}
  activeIdentity={activeIdentity}
  memory={memory}
  relationships={relationships}
  executionDecision={lastExecutionDecision}
  responsePolicyResult={lastResponsePolicyResult}
/>
        </div>
      )}
    </div>
  );
};

export default Chat;